import { createSlice, createAction, PayloadAction } from '@reduxjs/toolkit';
import { globalReset } from '@/store/actions';
import {
    VocabularyGroup,
    VocabularyGroupMembership,
    VocabularyGroupShare,
    CreateVocabularyGroupRequest,
    UpdateVocabularyGroupRequest,
    VocabularyGroupFilters,
    VocabularyGroupResponse,
    PaginationParams,
    VocabularyResponse,
} from '@/services/vocabularyService';
import { START_PAGE } from '@/common/constants';

// === VOCABULARY GROUPS ACTIONS ===
export const fetchVocabularyGroupsRequest = createAction<VocabularyGroupFilters | undefined>(
    'vocabularyGroups/fetchVocabularyGroupsRequest',
);
export const fetchVocabularyGroupsSuccess = createAction<VocabularyGroupResponse>(
    'vocabularyGroups/fetchVocabularyGroupsSuccess',
);
export const fetchVocabularyGroupsFailure = createAction<string>('vocabularyGroups/fetchVocabularyGroupsFailure');

export const fetchVocabularyGroupByIdRequest = createAction<string>('vocabularyGroups/fetchVocabularyGroupByIdRequest');
export const fetchVocabularyGroupByIdSuccess = createAction<VocabularyGroup>(
    'vocabularyGroups/fetchVocabularyGroupByIdSuccess',
);
export const fetchVocabularyGroupByIdFailure = createAction<string>('vocabularyGroups/fetchVocabularyGroupByIdFailure');

export const createVocabularyGroupRequest = createAction<CreateVocabularyGroupRequest>(
    'vocabularyGroups/createVocabularyGroupRequest',
);
export const createVocabularyGroupSuccess = createAction<VocabularyGroup>(
    'vocabularyGroups/createVocabularyGroupSuccess',
);
export const createVocabularyGroupFailure = createAction<string>('vocabularyGroups/createVocabularyGroupFailure');

export const updateVocabularyGroupRequest = createAction<UpdateVocabularyGroupRequest>(
    'vocabularyGroups/updateVocabularyGroupRequest',
);
export const updateVocabularyGroupSuccess = createAction<VocabularyGroup>(
    'vocabularyGroups/updateVocabularyGroupSuccess',
);
export const updateVocabularyGroupFailure = createAction<string>('vocabularyGroups/updateVocabularyGroupFailure');

export const deleteVocabularyGroupRequest = createAction<string>('vocabularyGroups/deleteVocabularyGroupRequest');
export const deleteVocabularyGroupSuccess = createAction<string>('vocabularyGroups/deleteVocabularyGroupSuccess');
export const deleteVocabularyGroupFailure = createAction<string>('vocabularyGroups/deleteVocabularyGroupFailure');

export const addWordsToVocabularyGroupRequest = createAction<{ groupId: string; wordIds: number[] }>(
    'vocabularyGroups/addWordsToVocabularyGroupRequest',
);
export const addWordsToVocabularyGroupSuccess = createAction<VocabularyGroup>(
    'vocabularyGroups/addWordsToVocabularyGroupSuccess',
);
export const addWordsToVocabularyGroupFailure = createAction<string>(
    'vocabularyGroups/addWordsToVocabularyGroupFailure',
);

export const removeWordsFromVocabularyGroupRequest = createAction<{ groupId: string; wordIds: number[] }>(
    'vocabularyGroups/removeWordsFromVocabularyGroupRequest',
);
export const removeWordsFromVocabularyGroupSuccess = createAction<VocabularyGroup>(
    'vocabularyGroups/removeWordsFromVocabularyGroupSuccess',
);
export const removeWordsFromVocabularyGroupFailure = createAction<string>(
    'vocabularyGroups/removeWordsFromVocabularyGroupFailure',
);

export const fetchWordsInVocabularyGroupRequest = createAction<{ groupId: string; pagination?: PaginationParams }>(
    'vocabularyGroups/fetchWordsInVocabularyGroupRequest',
);
export const fetchWordsInVocabularyGroupSuccess = createAction<VocabularyResponse>(
    'vocabularyGroups/fetchWordsInVocabularyGroupSuccess',
);
export const fetchWordsInVocabularyGroupFailure = createAction<string>(
    'vocabularyGroups/fetchWordsInVocabularyGroupFailure',
);

// === GROUP SHARING ACTIONS ===
export const shareVocabularyGroupRequest = createAction<{
    groupId: string;
    userId: number;
    permission: 'view' | 'edit' | 'admin';
}>('vocabularyGroups/shareVocabularyGroupRequest');
export const shareVocabularyGroupSuccess = createAction<VocabularyGroupShare>(
    'vocabularyGroups/shareVocabularyGroupSuccess',
);
export const shareVocabularyGroupFailure = createAction<string>('vocabularyGroups/shareVocabularyGroupFailure');

export const fetchGroupSharesRequest = createAction<string>('vocabularyGroups/fetchGroupSharesRequest');
export const fetchGroupSharesSuccess = createAction<VocabularyGroupShare[]>('vocabularyGroups/fetchGroupSharesSuccess');
export const fetchGroupSharesFailure = createAction<string>('vocabularyGroups/fetchGroupSharesFailure');

// === ENHANCED GROUP MANAGEMENT ACTIONS ===
export const reorderWordsInGroupRequest = createAction<{
    groupId: string;
    wordOrders: { wordId: string; order: number }[];
}>('vocabularyGroups/reorderWordsInGroupRequest');
export const reorderWordsInGroupSuccess = createAction<string>('vocabularyGroups/reorderWordsInGroupSuccess');
export const reorderWordsInGroupFailure = createAction<string>('vocabularyGroups/reorderWordsInGroupFailure');

export const moveWordsBetweenGroupsRequest = createAction<{
    sourceGroupId: string;
    targetGroupId: string;
    wordIds: string[];
}>('vocabularyGroups/moveWordsBetweenGroupsRequest');
export const moveWordsBetweenGroupsSuccess = createAction<{
    sourceGroupId: string;
    targetGroupId: string;
    wordIds: string[];
}>('vocabularyGroups/moveWordsBetweenGroupsSuccess');
export const moveWordsBetweenGroupsFailure = createAction<string>('vocabularyGroups/moveWordsBetweenGroupsFailure');

export const duplicateGroupRequest = createAction<{ groupId: string; newName: string }>(
    'vocabularyGroups/duplicateGroupRequest',
);
export const duplicateGroupSuccess = createAction<VocabularyGroup>('vocabularyGroups/duplicateGroupSuccess');
export const duplicateGroupFailure = createAction<string>('vocabularyGroups/duplicateGroupFailure');

// === STATE INTERFACE ===
export interface VocabularyGroupsState {
    vocabularyGroups: VocabularyGroup[];
    selectedVocabularyGroup: VocabularyGroup | null;
    groupMemberships: VocabularyGroupMembership[];
    groupShares: VocabularyGroupShare[];
    totalVocabularyGroup: number;
    currentPage: number;

    // Filters
    vocabularyGroupFilters: VocabularyGroupFilters;

    // Loading States
    isLoadingGroups: boolean;
    isCreatingGroup: boolean;
    isUpdatingGroup: boolean;
    isDeletingGroup: boolean;
    isReorderingWords: boolean;
    isMovingWords: boolean;
    isDuplicatingGroup: boolean;

    // Error States
    groupError: string | null;
    groupManagementError: string | null;
}

const initialState: VocabularyGroupsState = {
    vocabularyGroups: [],
    selectedVocabularyGroup: null,
    groupMemberships: [],
    groupShares: [],
    totalVocabularyGroup: 0,
    currentPage: START_PAGE,
    vocabularyGroupFilters: {},
    isLoadingGroups: false,
    isCreatingGroup: false,
    isUpdatingGroup: false,
    isDeletingGroup: false,
    isReorderingWords: false,
    isMovingWords: false,
    isDuplicatingGroup: false,
    groupError: null,
    groupManagementError: null,
};

const vocabularyGroupsSlice = createSlice({
    name: 'vocabularyGroups',
    initialState,
    reducers: {
        setCurrentPage: (state, action) => {
            state.currentPage = action.payload;
        },
        setVocabularyGroupFilters: (state, action: PayloadAction<VocabularyGroupFilters>) => {
            state.vocabularyGroupFilters = action.payload;
        },
        clearGroupFilters: (state) => {
            state.vocabularyGroupFilters = {};
        },
        setSelectedVocabularyGroup: (state, action: PayloadAction<VocabularyGroup | null>) => {
            state.selectedVocabularyGroup = action.payload;
        },
        clearGroupError: (state) => {
            state.groupError = null;
        },
        clearGroupManagementError: (state) => {
            state.groupManagementError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(globalReset, () => initialState)

            // === FETCH VOCABULARY GROUPS ===
            .addCase(fetchVocabularyGroupsRequest, (state, action) => {
                state.isLoadingGroups = true;
                state.groupError = null;
                if (action.payload) {
                    state.vocabularyGroupFilters = action.payload;
                }
            })
            .addCase(fetchVocabularyGroupsSuccess, (state, action) => {
                state.isLoadingGroups = false;
                state.vocabularyGroups = action.payload.results;
                state.totalVocabularyGroup = action.payload.count;
            })
            .addCase(fetchVocabularyGroupsFailure, (state, action) => {
                state.isLoadingGroups = false;
                state.groupError = action.payload;
            })

            // === FETCH VOCABULARY GROUP BY ID ===
            .addCase(fetchVocabularyGroupByIdRequest, (state) => {
                state.isLoadingGroups = true;
                state.groupError = null;
            })
            .addCase(fetchVocabularyGroupByIdSuccess, (state, action) => {
                state.isLoadingGroups = false;
                state.selectedVocabularyGroup = action.payload;
                // Update group in groups array if it exists
                const index = state.vocabularyGroups.findIndex((group) => group.id === action.payload.id);
                if (index !== -1) {
                    state.vocabularyGroups[index] = action.payload;
                }
            })
            .addCase(fetchVocabularyGroupByIdFailure, (state, action) => {
                state.isLoadingGroups = false;
                state.groupError = action.payload;
            })

            // === CREATE VOCABULARY GROUP ===
            .addCase(createVocabularyGroupRequest, (state) => {
                state.isCreatingGroup = true;
                state.groupError = null;
            })
            .addCase(createVocabularyGroupSuccess, (state, action) => {
                state.isCreatingGroup = false;
                state.vocabularyGroups.unshift(action.payload);
            })
            .addCase(createVocabularyGroupFailure, (state, action) => {
                state.isCreatingGroup = false;
                state.groupError = action.payload;
            })

            // === UPDATE VOCABULARY GROUP ===
            .addCase(updateVocabularyGroupRequest, (state) => {
                state.isUpdatingGroup = true;
                state.groupError = null;
            })
            .addCase(updateVocabularyGroupSuccess, (state, action) => {
                state.isUpdatingGroup = false;
                const index = state.vocabularyGroups.findIndex((group) => group.id === action.payload.id);
                if (index !== -1) {
                    state.vocabularyGroups[index] = action.payload;
                }
                if (state.selectedVocabularyGroup?.id === action.payload.id) {
                    state.selectedVocabularyGroup = action.payload;
                }
            })
            .addCase(updateVocabularyGroupFailure, (state, action) => {
                state.isUpdatingGroup = false;
                state.groupError = action.payload;
            })

            // === DELETE VOCABULARY GROUP ===
            .addCase(deleteVocabularyGroupRequest, (state) => {
                state.isDeletingGroup = true;
                state.groupError = null;
            })
            .addCase(deleteVocabularyGroupSuccess, (state, action) => {
                state.isDeletingGroup = false;
                state.vocabularyGroups = state.vocabularyGroups.filter((group) => group.id !== action.payload);
                if (state.selectedVocabularyGroup?.id === action.payload) {
                    state.selectedVocabularyGroup = null;
                }
            })
            .addCase(deleteVocabularyGroupFailure, (state, action) => {
                state.isDeletingGroup = false;
                state.groupError = action.payload;
            })

            // === ADD WORDS TO VOCABULARY GROUP ===
            .addCase(addWordsToVocabularyGroupRequest, (state) => {
                state.isUpdatingGroup = true;
                state.groupError = null;
            })
            .addCase(addWordsToVocabularyGroupSuccess, (state, action) => {
                state.isUpdatingGroup = false;
                const index = state.vocabularyGroups.findIndex((group) => group.id === action.payload.id);
                if (index !== -1) {
                    state.vocabularyGroups[index] = action.payload;
                }
                if (state.selectedVocabularyGroup?.id === action.payload.id) {
                    state.selectedVocabularyGroup = action.payload;
                }
            })
            .addCase(addWordsToVocabularyGroupFailure, (state, action) => {
                state.isUpdatingGroup = false;
                state.groupError = action.payload;
            })

            // === REMOVE WORDS FROM VOCABULARY GROUP ===
            .addCase(removeWordsFromVocabularyGroupRequest, (state) => {
                state.isUpdatingGroup = true;
                state.groupError = null;
            })
            .addCase(removeWordsFromVocabularyGroupSuccess, (state, action) => {
                state.isUpdatingGroup = false;
                const index = state.vocabularyGroups.findIndex((group) => group.id === action.payload.id);
                if (index !== -1) {
                    state.vocabularyGroups[index] = action.payload;
                }
                if (state.selectedVocabularyGroup?.id === action.payload.id) {
                    state.selectedVocabularyGroup = action.payload;
                }
            })
            .addCase(removeWordsFromVocabularyGroupFailure, (state, action) => {
                state.isUpdatingGroup = false;
                state.groupError = action.payload;
            })

            // === GROUP SHARING ===
            .addCase(shareVocabularyGroupRequest, (state) => {
                state.isLoadingGroups = true;
                state.groupError = null;
            })
            .addCase(shareVocabularyGroupSuccess, (state, action) => {
                state.isLoadingGroups = false;
                state.groupShares.push(action.payload);
            })
            .addCase(shareVocabularyGroupFailure, (state, action) => {
                state.isLoadingGroups = false;
                state.groupError = action.payload;
            })

            .addCase(fetchGroupSharesRequest, (state) => {
                state.isLoadingGroups = true;
                state.groupError = null;
            })
            .addCase(fetchGroupSharesSuccess, (state, action) => {
                state.isLoadingGroups = false;
                state.groupShares = action.payload;
            })
            .addCase(fetchGroupSharesFailure, (state, action) => {
                state.isLoadingGroups = false;
                state.groupError = action.payload;
            })

            // === ENHANCED GROUP MANAGEMENT ===
            .addCase(reorderWordsInGroupRequest, (state) => {
                state.isReorderingWords = true;
                state.groupManagementError = null;
            })
            .addCase(reorderWordsInGroupSuccess, (state) => {
                state.isReorderingWords = false;
            })
            .addCase(reorderWordsInGroupFailure, (state, action) => {
                state.isReorderingWords = false;
                state.groupManagementError = action.payload;
            })

            .addCase(moveWordsBetweenGroupsRequest, (state) => {
                state.isMovingWords = true;
                state.groupManagementError = null;
            })
            .addCase(moveWordsBetweenGroupsSuccess, (state) => {
                state.isMovingWords = false;
            })
            .addCase(moveWordsBetweenGroupsFailure, (state, action) => {
                state.isMovingWords = false;
                state.groupManagementError = action.payload;
            })

            .addCase(duplicateGroupRequest, (state) => {
                state.isDuplicatingGroup = true;
                state.groupManagementError = null;
            })
            .addCase(duplicateGroupSuccess, (state, action) => {
                state.isDuplicatingGroup = false;
                state.vocabularyGroups.push(action.payload);
            })
            .addCase(duplicateGroupFailure, (state, action) => {
                state.isDuplicatingGroup = false;
                state.groupManagementError = action.payload;
            });
    },
});

export const {
    setCurrentPage,
    setVocabularyGroupFilters,
    clearGroupFilters,
    setSelectedVocabularyGroup,
    clearGroupError,
    clearGroupManagementError,
} = vocabularyGroupsSlice.actions;

export default vocabularyGroupsSlice.reducer;
